import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import TID from "@/lib/testIds";

const testimonials = [
  {
    name: "Rahul Mehta",
    role: "CTO, Finstack Labs",
    quote:
      "Their pentest report was the most thorough we've ever received. Clear, prioritized, and actionable — exactly what our board needed.",
  },
  {
    name: "Anjali Sharma",
    role: "Founder, Bloom Skincare",
    quote:
      "ROAS jumped from 1.6x to 5.4x in 60 days. The creative, targeting, and reporting are on another level.",
  },
  {
    name: "Daniel Park",
    role: "Head of Engineering, Cargo One",
    quote:
      "API security testing uncovered issues we'd missed for years. Pragmatic engineers who actually understand modern stacks.",
  },
  {
    name: "Priya Iyer",
    role: "Marketing Director, Lumi Home",
    quote:
      "Our new website loads in under a second and converts 2.3x better. The launch was buttery smooth.",
  },
  {
    name: "Omar Khan",
    role: "Founder, Latitude Studio",
    quote:
      "Felt like having an in-house growth team. Honest reporting, proactive ideas, no fluff.",
  },
  {
    name: "Sofia Romero",
    role: "VP Product, Northwind",
    quote:
      "Premium polish without enterprise overhead. They moved fast and treated our codebase with respect.",
  },
];

export const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const total = testimonials.length;

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % total), 5500);
    return () => clearInterval(t);
  }, [total]);

  const go = (dir) => setIndex((i) => (i + dir + total) % total);

  return (
    <section
      id="testimonials"
      data-testid={TID.testimonialsSection}
      className="relative py-24 md:py-32 bg-gradient-to-b from-white to-slate-50 dark:from-[#0B1120] dark:to-[#0B1120]/80"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-brand-purple uppercase tracking-[0.18em]">Testimonials</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
              Trusted by founders & teams
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              data-testid={TID.testimonialPrev}
              onClick={() => go(-1)}
              aria-label="Previous testimonial"
              className="w-11 h-11 rounded-full border border-slate-200 dark:border-white/10 inline-flex items-center justify-center hover:bg-slate-100 dark:hover:bg-white/10 transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid={TID.testimonialNext}
              onClick={() => go(1)}
              aria-label="Next testimonial"
              className="w-11 h-11 rounded-full border border-slate-200 dark:border-white/10 inline-flex items-center justify-center hover:bg-slate-100 dark:hover:bg-white/10 transition"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="relative mt-12 grid md:grid-cols-3 gap-6">
          {[0, 1, 2].map((offset) => {
            const t = testimonials[(index + offset) % total];
            return (
              <AnimatePresence mode="wait" key={offset}>
                <motion.article
                  key={`${index}-${offset}`}
                  data-testid={TID.testimonialCard(offset + 1)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, delay: offset * 0.1 }}
                  className="rounded-3xl p-7 md:p-8 bg-white dark:bg-[#0F172A]/70 border border-slate-200 dark:border-white/10 shadow-elevated"
                >
                  <Quote className="w-7 h-7 text-brand-blue/70" />
                  <p className="mt-4 text-slate-700 dark:text-slate-200 leading-relaxed text-[15px]">
                    “{t.quote}”
                  </p>
                  <div className="mt-6 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{t.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{t.role}</p>
                    </div>
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                  </div>
                </motion.article>
              </AnimatePresence>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
